import React, { useState } from 'react';
import {
  isCloudAuthoritativeSession,
  loginErpSession,
  logoutErpSession
} from '../services/erpSessionClient';
import {
  ERP_USERNAME_HELP,
  isValidErpUsername,
  normalizeErpUsername
} from '../services/erpUsername';
import { provisionalCentralUser } from '../services/centralSessionUser';
import '../styles/Login.css';

export default function Login({ onLogin }) {
  const [tenantSlug, setTenantSlug] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [offlineAvailable, setOfflineAvailable] = useState(false);
  const isElectron = window.electron?.runtime === 'electron';

  const credentials = () => ({
    tenantSlug: tenantSlug.trim().toLowerCase(),
    username: normalizeErpUsername(username),
    password
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const acceptedCredentials = credentials();
    if (!isValidErpUsername(acceptedCredentials.username)) {
      setError(ERP_USERNAME_HELP);
      return;
    }
    setLoading(true);
    setError('');
    setOfflineAvailable(false);
    let browserCentralSessionCreated = false;

    try {
      if (isElectron) {
        const result = await window.electron.auth.login(
          acceptedCredentials.tenantSlug,
          acceptedCredentials.username,
          acceptedCredentials.password
        );
        if (!result?.ok || !result.user) {
          setOfflineAvailable(result?.offlineAvailable === true);
          setError(result?.message || 'Não foi possível entrar.');
          return;
        }
        onLogin(result.user);
        return;
      }

      const actor = await loginErpSession({
        ...acceptedCredentials,
        clientKind: 'web',
        deviceLabel: navigator.userAgent
      });
      if (!isCloudAuthoritativeSession(actor)) {
        await logoutErpSession().catch(() => {});
        throw new Error('Esta licenca esta no modo Local. Use o aplicativo Nivora ERP instalado no computador autorizado; o acesso pelo navegador exige o plano Nuvem.');
      }
      browserCentralSessionCreated = true;
      const adopted = await window.electron?.auth?.adoptCentralActor?.(actor);
      onLogin(adopted?.ok && adopted.user
        ? adopted.user
        : provisionalCentralUser(actor));
    } catch (loginError) {
      if (browserCentralSessionCreated) await logoutErpSession().catch(() => {});
      setError(loginError?.message || 'Não foi possível entrar. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleOfflineLogin = async () => {
    const acceptedCredentials = credentials();
    if (!isValidErpUsername(acceptedCredentials.username)) {
      setError(ERP_USERNAME_HELP);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await window.electron.auth.loginOffline(
        acceptedCredentials.tenantSlug,
        acceptedCredentials.username,
        acceptedCredentials.password
      );
      if (!result?.ok || !result.user) {
        setError(result?.message || 'Não foi possível abrir o acesso offline.');
        return;
      }
      onLogin(result.user);
    } catch (offlineError) {
      setError(offlineError?.message || 'Não foi possível abrir o acesso offline.');
    } finally {
      setLoading(false);
    }
  };

  const defaultLogo = `${process.env.PUBLIC_URL || ''}/assets/nivora-logo.png`;

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <img className="login-product-logo" src={defaultLogo} alt="Nivora ERP" />
          <p>Acesse a conta da sua empresa</p>
        </div>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="login-tenant">Empresa</label>
            <input
              id="login-tenant"
              type="text"
              className="form-control"
              value={tenantSlug}
              onChange={(event) => {
                setTenantSlug(event.target.value);
                setOfflineAvailable(false);
              }}
              autoComplete="organization"
              placeholder="ex.: minha-empresa"
              pattern="[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*"
              maxLength="80"
              aria-describedby="login-tenant-help"
              required
              autoFocus
            />
            <small id="login-tenant-help" className="login-field-help">
              Use o identificador informado na ativação da empresa.
            </small>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="login-username">Usuário ou e-mail</label>
            <input
              id="login-username"
              type="text"
              className="form-control"
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
                setOfflineAvailable(false);
              }}
              autoComplete="username"
              maxLength="80"
              aria-describedby="login-username-help"
              required
            />
            <small id="login-username-help" className="login-field-help">{ERP_USERNAME_HELP}</small>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="login-password">Senha</label>
            <input
              id="login-password"
              type="password"
              className="form-control"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setOfflineAvailable(false);
              }}
              autoComplete="current-password"
              maxLength="200"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        {!isElectron && (
          <div className="login-access-links">
            <a href="/login#/esqueci-senha">Esqueci minha senha</a>
            <a href="/login#/criar-conta">Crie sua conta agora — teste grátis por 30 dias</a>
          </div>
        )}

        {isElectron && offlineAvailable && (
          <div className="login-contingency" role="status">
            <p>
              O servidor central não respondeu. A licença segura deste computador permite
              continuar trabalhando offline e sincronizar quando a conexão voltar.
            </p>
            <button type="button" className="btn btn-outline" disabled={loading} onClick={handleOfflineLogin}>
              Entrar no modo offline
            </button>
          </div>
        )}

        <p className="login-help">
          Cada pessoa deve usar sua própria conta. Ao entrar em outro dispositivo, o acesso anterior é encerrado.
        </p>
      </div>
    </div>
  );
}
